// Story 6.1 (ADO-877) — Bearer-attaching API request layer (AC3).
//
// ┌─ SEAM ─────────────────────────────────────────────────────────────────────┐
// │ This is the SINGLE seam the future `openapi-typescript`-generated client    │
// │ will plug into. Per project-context the generated client lives at THIS path │
// │ (`apps/web/src/api/shoplink-api.ts`) and is "regenerated by the Nx pipeline │
// │ … never hand-edit". That pipeline + the `/openapi/v1.json` spec do NOT      │
// │ exist in the repo yet (Story 6.1 deviation D-1), so this hand-authored thin │
// │ wrapper is a DELIBERATE, documented placeholder — NOT a full client surface.│
// │ When generation lands, the generated client consumes `apiFetch` (or its     │
// │ access-token provider) as its transport; do not invent endpoints here.      │
// └─────────────────────────────────────────────────────────────────────────────┘
//
// AC3: every `/api/v1/*` request carries `Authorization: Bearer <access_token>`.
// The `/api/v1` half of D-1 is CLOSED: the API serves the global prefix natively
// (`apps/api/src/global-prefix.ts`, ADO-1181), so these paths hit live routes
// wherever the backend endpoint exists (one consumer, `tenant-context.client.ts`,
// targets a membership read endpoint not built yet — it degrades gracefully).
// The generated-client half (openapi-typescript + `/openapi/v1.json`) remains
// deferred. The testable core delivered here is the Bearer-attaching transport
// (unit-tested with msw).

/** The versioned API path prefix all backend calls share (project-context API design). */
export const API_PREFIX = '/api/v1';

/**
 * The `x-shoplink-problem-type` header slug the API sets on a TERMINAL 403
 * (mirrors `apps/api` `ACCOUNT_DEACTIVATED_PROBLEM` / `NO_ACCESS_PROBLEM`). Both
 * a deactivated principal AND an authenticated-but-unprovisioned one (no SL2
 * membership row, Story 6.32 / ADO-1315) are TERMINAL — the session must end,
 * NOT be silently refreshed/retried like an expiry 401 (the Auth0 session is
 * still valid, so a refresh would succeed and the request would loop into a
 * "logged-in but every call 403s" dead-end). Detected here so it is handled
 * once, centrally, before the 403 reaches any caller.
 */
export const PROBLEM_TYPE_HEADER = 'x-shoplink-problem-type';
export const DEACTIVATED_PROBLEM_TYPE = 'account-deactivated';
export const NO_ACCESS_PROBLEM_TYPE = 'no-access';

/** The problem-type slugs that must end the session rather than refresh/retry. */
export const TERMINAL_PROBLEM_TYPES: readonly string[] = [
  DEACTIVATED_PROBLEM_TYPE,
  NO_ACCESS_PROBLEM_TYPE,
];

/** Resolves the current access token, or `null` when unauthenticated/unconfigured. */
export type AccessTokenProvider = () => Promise<string | null>;

/**
 * Story 6.3 (ADO-879, AC1/AC2) — forces a fresh token after a 401. Resolves to the
 * new token on a successful silent refresh, or `null` when the refresh fails. On a
 * TERMINAL failure the registered refresher also clears local session state and
 * redirects to Universal Login (that side effect lives with the AuthProvider, which
 * owns the Auth0 client + stores — this transport stays decoupled from both).
 */
export type AccessTokenRefresher = () => Promise<string | null>;

// Default: no token. The AuthProvider registers the real provider once an Auth0
// client exists (see registerAccessTokenProvider). Keeping this indirection here
// (rather than importing the Auth0 client) keeps this transport decoupled from
// the identity layer — the generated client will inherit the same seam.
let accessTokenProvider: AccessTokenProvider = async () => null;

// Story 6.3 (D-3): the post-401 forced-refresh hook. null until the AuthProvider
// wires it; when null, a 401 is surfaced unchanged (the Story 6.1 behaviour).
let accessTokenRefresher: AccessTokenRefresher | null = null;

/**
 * Invoked once when the API reports the principal is deactivated (403 with
 * {@link DEACTIVATED_PROBLEM_TYPE}). The AuthProvider wires it to a full sign-out
 * (clear local session + end the Auth0 session → branded login), which breaks the
 * silent re-auth loop a deactivated user would otherwise hit. null until wired.
 */
export type SessionTerminationHandler = () => void;
let sessionTerminationHandler: SessionTerminationHandler | null = null;
// Single-flight: many in-flight calls can 403 at once; terminate the session only
// once (logout navigates away — a second call would be redundant/racey).
let sessionTerminated = false;

/** Register the deactivation-termination hook (full sign-out). */
export function registerSessionTerminationHandler(handler: SessionTerminationHandler): void {
  sessionTerminationHandler = handler;
}

/** Register the function used to obtain the bearer token for `/api/v1/*` calls. */
export function registerAccessTokenProvider(provider: AccessTokenProvider): void {
  accessTokenProvider = provider;
}

/** Register the post-401 forced-refresh hook (Story 6.3, AC1). */
export function registerAccessTokenRefresher(refresher: AccessTokenRefresher): void {
  accessTokenRefresher = refresher;
}

/**
 * Reset the token seams to the unauthenticated default (used on logout / terminal
 * refresh failure / in tests). Clears BOTH the provider and the refresher so no
 * stale closure keeps handing out tokens after the session ends.
 */
export function resetAccessTokenProvider(): void {
  accessTokenProvider = async () => null;
  accessTokenRefresher = null;
  sessionTerminationHandler = null;
  sessionTerminated = false;
}

/**
 * Story 7.3 (ADO-1139) — read the current access token through the SAME
 * registered provider `apiFetch` uses. The Socket.io availability handshake
 * carries the REST JWT in its `auth` payload (architecture §real-time: one
 * shared connection per session, authed with the REST JWT), so the socket
 * layer needs the token itself rather than a fetch wrapper. Resolves `null`
 * when unauthenticated/unconfigured — callers must not connect in that case.
 */
export async function currentAccessToken(): Promise<string | null> {
  return accessTokenProvider();
}

/**
 * Whether a request targets the versioned API on THIS origin. Handles both
 * relative (`/api/v1/...`) and absolute (`https://host/api/v1/...`) forms by
 * resolving the URL, then requires BOTH:
 *   - same-origin as `window.location.origin` — the API is served on the same
 *     host as the SPA (ALB/CloudFront topology), so the bearer token is NEVER
 *     attached to a foreign origin (token-leak guard);
 *   - an exact `/api/v1` path segment — so look-alikes like `/api/v10` or
 *     `/api/v1foo` do not match.
 * If a separate API origin is ever introduced, extend this to an allowlist.
 */
function isApiPath(path: string): boolean {
  const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
  let url: URL;
  try {
    url = new URL(path, origin);
  } catch {
    return false;
  }
  // Fail closed: without a known origin (no `window`) we cannot prove the
  // request is same-origin, so never attach the bearer (token-leak guard).
  if (origin === undefined) return false;
  if (url.origin !== origin) return false;
  return url.pathname === API_PREFIX || url.pathname.startsWith(`${API_PREFIX}/`);
}

/**
 * Story 6.25 (ADO-1204, AC3) — whether `init.body` can be safely re-sent on the
 * post-401 refresh-retry. Conservative ALLOWLIST: a body is replayable only when
 * it is a value fetch can serialise again from the same reference — no body
 * (`undefined`/`null`), a `string`, `URLSearchParams`, a `Blob` (immutable),
 * `ArrayBuffer`, or an `ArrayBufferView` (TypedArray/DataView). A one-shot
 * `ReadableStream` (consumed on the first send), a `FormData` (conservatively
 * treated as one-shot — see the story Decision of Record), and any unknown future
 * body type are NOT replayable, so the retry is skipped and the original 401 is
 * surfaced rather than replaying a broken/opaque body. Exported for unit tests.
 *
 * Global `instanceof` checks are guarded with a `typeof` existence probe so a
 * missing global (SSR / minimal jsdom) fails closed to "not replayable" instead
 * of throwing — the same fail-closed posture as {@link isApiPath}'s `window` guard.
 */
export function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body === undefined || body === null) return true;
  if (typeof body === 'string') return true;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return true;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return true;
  if (typeof ArrayBuffer !== 'undefined') {
    if (body instanceof ArrayBuffer) return true;
    if (ArrayBuffer.isView(body)) return true;
  }
  // ReadableStream, FormData, and anything unknown: treat as one-shot / not
  // provably replayable.
  return false;
}

/**
 * Fetch wrapper that attaches `Authorization: Bearer <token>` to every
 * `/api/v1/*` request. Non-API paths pass through untouched.
 *
 * Story 6.3 (AUTH_FLOW_SPEC §8, AC1/AC2): on a `401` from the versioned API, a
 * single silent refresh is attempted via the registered refresher; on success the
 * original request is retried ONCE with the new bearer. The retry re-issues
 * directly (not through `apiFetch`), so a `401` on the retry is surfaced as-is —
 * there is never a second refresh (AC2: no infinite loop). A failed/terminal
 * refresh is handled inside the refresher (clear session + redirect to Universal
 * Login); here it simply yields `null` and the original `401` stands.
 *
 * Story 6.25 (ADO-1204, AC3/AC4): the retry re-sends `init.body`, so it is only
 * issued when the body is provably REPLAYABLE ({@link isReplayableBody}). Today's
 * callers (GETs / string bodies) are all replayable, so their behaviour is
 * unchanged; a future one-shot `ReadableStream`/`FormData` body is NOT replayed —
 * the original 401 is surfaced instead of a broken retry. The retry is gated on
 * replayability rather than on idempotent methods deliberately: a 401 is produced
 * by JwtAuthGuard BEFORE any handler runs, so the rejected request performed no
 * write — replaying a replayable-bodied non-idempotent POST after refresh is safe
 * (no double-write) and desirable, and restricting to idempotent methods would
 * needlessly fail a recoverable POST. The hazard is body replayability, not
 * method idempotency.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const isApi = isApiPath(path);

  const issue = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (isApi && token !== null && token !== '') {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return fetch(path, { ...init, headers });
  };

  // Terminal: the principal is deactivated OR unprovisioned for SL2 (403 +
  // terminal problem-type header, Story 6.32 / ADO-1315). End the session ONCE
  // (full sign-out) rather than refreshing/retrying — the Auth0 session is still
  // valid, so a refresh would succeed and the request would loop. The 403 is
  // still returned so the in-flight caller unwinds normally. This must run on
  // BOTH the initial response AND the post-refresh retry: an expired token makes
  // JwtAuthGuard 401 first (the registry check never runs), so the terminal 403
  // only surfaces on the retried request once the refreshed token is valid.
  const terminateIfSessionEnded = (response: Response): void => {
    if (
      isApi &&
      response.status === 403 &&
      TERMINAL_PROBLEM_TYPES.includes(response.headers.get(PROBLEM_TYPE_HEADER) ?? '') &&
      !sessionTerminated &&
      sessionTerminationHandler !== null
    ) {
      sessionTerminated = true;
      sessionTerminationHandler();
    }
  };

  const token = isApi ? await accessTokenProvider() : null;
  const response = await issue(token);
  terminateIfSessionEnded(response);

  if (isApi && response.status === 401 && accessTokenRefresher !== null) {
    const refreshed = await accessTokenRefresher();
    // Story 6.25 (ADO-1204) — only replay the retry when the body can be safely
    // re-sent; a one-shot/unknown body is not replayed (surface the original 401).
    if (refreshed !== null && refreshed !== '' && isReplayableBody(init.body)) {
      const retried = await issue(refreshed);
      terminateIfSessionEnded(retried);
      return retried;
    }
  }

  return response;
}
